import React, { useState, useEffect, useCallback } from "react";
import { Button, Box, Tabs, Tab } from "@mui/material";
import dayjs from "dayjs";

import DataTable from "../../../../pages/Requirement/components/common/ReusableTable";
import SearchFilter from "../../../../pages/Requirement/components/common/SearchFilter";
import AlertModal from "../../../../pages/Requirement/components/common/AlerModel";
import OfferDetails from "./OfferDetails";
import CreateOfferForm from "./CreateOfferForm";
import {
  fetchData,
  postQuery,
} from "../../../../pages/Requirement/services/RequirementServices";
import { useLoading } from "../../../../context/LoadingContext";
import {
  openAgreementPopup,
  extractOfferDataForAgreement,
} from "../../../../services/AgreementTemplate";

const MatchingOffers = ({
  requirementDetails,
  allProductMaster = [],
  filterType = "Matched", // Add filterType prop
  onRequirementsRefresh, // Add callback to refresh parent requirements table
  onDeselectRequirement, // Add callback to deselect parent requirement
  userType, // Add userType prop
}) => {
  const { setLoading } = useLoading();
  const [offerList, setOfferList] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [productDetails, setProductDetails] = useState({});
  const [referenceOffers, setReferenceOffers] = useState([]);
  const [searchValues, setSearchValues] = useState({
    searchPriceRange: "",
  });
  const [showCreateOfferForm, setShowCreateOfferForm] = useState(false);
  const [selectedReferenceOffer, setSelectedReferenceOffer] = useState(null); // Track which reference offer to use for creating new offer
  const [tabValue, setTabValue] = useState(0);
  const [previousTabValue, setPreviousTabValue] = useState(0); // Track previous tab to return to after form submission

  console.log("filterType values:", filterType);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: false,
  });

  // TabPanel component for tab content
  const TabPanel = ({ children, value, index }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
      >
        {value === index && <Box>{children}</Box>}
      </div>
    );
  };

  // Handle tab change
  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  // Helper function to determine which tab should be selected based on ReferenceOfferID
  const determineSelectedTab = useCallback((selectedOffer, referenceOffers) => {
    console.log("Determining tab selection:", {
      selectedOffer: selectedOffer,
      referenceOfferID: selectedOffer?.ReferenceOfferID,
      referenceOffers: referenceOffers,
    });

    // If selectedOffer has ReferenceOfferID, find the matching reference offer tab
    if (selectedOffer?.ReferenceOfferID) {
      const referenceIndex = referenceOffers.findIndex(
        (offer) => offer.GPOfferID === selectedOffer.ReferenceOfferID
      );
      console.log("Reference index found:", referenceIndex);

      // If found, select that tab (index + 1 because tab 0 is the main offer)
      if (referenceIndex !== -1) {
        console.log("Selecting reference tab:", referenceIndex + 1);
        return referenceIndex + 1;
      }
    }
    // Default to main offer tab (index 0)
    console.log("Selecting main offer tab: 0");
    return 0;
  }, []);

  // Helper function to determine which tab should show the download button
  const shouldShowDownloadButton = useCallback(
    (tabIndex, selectedOffer, referenceOffers) => {
      console.log(
        "Checking download button for tab:",
        tabIndex,
        "selectedOffer ReferenceOfferID:",
        selectedOffer?.ReferenceOfferID,
        referenceOffers,
        selectedOffer
      );

      // If selectedOffer has ReferenceOfferID, find the matching reference offer tab
      if (selectedOffer?.ReferenceOfferID) {
        const referenceIndex = referenceOffers.findIndex(
          (offer) => offer.GPOfferID === selectedOffer.ReferenceOfferID
        );
        console.log(
          "Reference index found:",
          referenceIndex,
          "for ReferenceOfferID:",
          selectedOffer.ReferenceOfferID
        );

        // If found, show button on that reference tab (index + 1 because tab 0 is the main offer)
        if (referenceIndex !== -1) {
          const shouldShow = tabIndex === referenceIndex + 1;
          console.log(
            `Tab ${tabIndex} should show download button:`,
            shouldShow,
            "(reference tab:",
            referenceIndex + 1,
            ")"
          );
          return shouldShow;
        }
      }
      // Default to main offer tab (index 0)
      const shouldShow = tabIndex === 0;
      console.log(
        `Tab ${tabIndex} should show download button:`,
        shouldShow,
        "(main offer tab)"
      );
      return shouldShow;
    },
    []
  );

  // Helper function to determine which tab should show the create offer button
  const shouldShowCreateOfferButton = useCallback(
    (tabIndex, referenceOffers) => {
      console.log(
        "Checking create offer button for tab:",
        tabIndex,
        "referenceOffers:",
        referenceOffers
      );

      // Only show for reference offer tabs (not main offer tab)
      if (tabIndex === 0) return false;

      const referenceIndex = tabIndex - 1; // Convert tab index to reference offer index
      const isLastReferenceOffer =
        referenceIndex === referenceOffers.length - 1;
      const referenceOffer = referenceOffers[referenceIndex];
      const hasIsFirmReferenced =
        referenceOffer?.BuyerExtendedStatus === "Firmoffer" ||
        referenceOffer?.BuyerExtendedStatus === "BAFO" ||
        referenceOffer?.GPExtendedStatus === "Firmoffer" ||
        referenceOffer?.GPExtendedStatus === "BAFO";

      console.log(
        `Tab ${tabIndex} - isLastReferenceOffer:`,
        isLastReferenceOffer,
        "hasIsFirmReferenced:",
        hasIsFirmReferenced,
        "referenceOffer.IsFirmReferenced:",
        referenceOffer?.IsFirmReferenced
      );

      return isLastReferenceOffer && hasIsFirmReferenced;
    },
    []
  );

  const priceOptions = [
    { value: "", label: "Select Range" },
    { value: "0-1000", label: "0-1000 USD" },
    { value: "1000-2000", label: "1000-2000 USD" },
    { value: "2000-5000", label: "2000-5000 USD" },
    { value: "Above-5000", label: "Above 5000 USD" },
  ];

  const handleModalClose = () => {
    setModal({
      show: false,
      type: "info",
      title: "",
      message: "",
      autoClose: false,
    });
  };

  const getRequirementProductInfo = useCallback(() => {
    if (
      requirementDetails &&
      requirementDetails.ProductID &&
      allProductMaster.length > 0
    ) {
      // Find product details from allProductMaster
      const productInfo = allProductMaster.find(
        (item) => item.ProductID === requirementDetails.ProductID
      );

      if (productInfo) {
        setProductDetails({
          ProductName: productInfo.ProductName,
          Grade: productInfo.Grade,
          ProductID: productInfo.ProductID,
        });
        console.log("Product details set:", {
          ProductName: productInfo.ProductName,
          Grade: productInfo.Grade,
          ProductID: productInfo.ProductID,
        });
      } else {
        console.log(
          "Product not found in allProductMaster for ProductID:",
          requirementDetails.ProductID
        );
        setProductDetails({});
      }
    } else {
      console.log(
        "Missing data - requirementDetails:",
        !!requirementDetails,
        "ProductID:",
        requirementDetails?.ProductID,
        "allProductMaster length:",
        allProductMaster.length
      );
      setProductDetails({});
    }
  }, [requirementDetails, allProductMaster]);

  // Format price helper
  const formatPrice = (price) => {
    if (!price || isNaN(price)) return "0";
    return parseFloat(price).toLocaleString();
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return dayjs(dateString).format("DD/MM/YYYY");
    } catch (error) {
      return dateString;
    }
  };

  const getReferenceOffers = useCallback(
    async (rowSelected) => {
      setReferenceOffers([]); // Clear previous offers
      try {
        setLoading(true);
        console.log("Fetching reference offers...", rowSelected);
        const body = {
          //target: "listGPOfferAdminDetails",
          target:
            userType === "Buyer"
              ? "listGPOfferAdminBuyer"
              : "listGPOfferAdminGP",
          userOrder: "CreatedAt ASC",
          rowStart: 0,
          pageSize: 50,
          filterTerms: {
            GPOOfferStatus: "Firmoffer",
            ReferenceOffer: rowSelected.GPOfferID,
            RequirementsID: requirementDetails.RequirementsID,
          },
        };

        const response = await fetchData(body);
        console.log(response.items);

        if (response.items && response.items.length > 0) {
          // Deduplicate based on GPOfferID to avoid showing same offer multiple times
          const uniqueOffers = response.items.reduce((acc, current) => {
            const existingOffer = acc.find(
              (offer) => offer.GPOfferID === current.GPOfferID
            );
            if (!existingOffer) {
              acc.push(current);
            }
            return acc;
          }, []);

          console.log("Unique offers:", uniqueOffers);
          setReferenceOffers(uniqueOffers);
          setLoading(false);
          return uniqueOffers; // Return the loaded offers
        }
        setLoading(false);
        return []; // Return empty array if no offers
      } catch (error) {
        console.error("Error fetching requirement details:", error);
        setReferenceOffers([]);
        setLoading(false);
        return []; // Return empty array on error
      }
    },
    [requirementDetails.RequirementsID, setLoading]
  );

  // Fetch matching offers
  const fetchMatchingOffers = useCallback(
    async (filters = {}, startRow = 0) => {
      if (!productDetails.ProductName || !productDetails.Grade) {
        console.log(
          "Product details not available yet - ProductName:",
          productDetails.ProductName,
          "Grade:",
          productDetails.Grade
        );
        return;
      }

      console.log(
        "Fetching offers for Product:",
        productDetails.ProductName,
        "Grade:",
        productDetails.Grade
      );
      setLoading(true);
      try {
        let filterTerms = {};

        // Also add alternative filtering if direct product name match doesn't work
        if (requirementDetails?.ProductID) {
          filterTerms.ProductID = requirementDetails.ProductID;
        }
        if (requirementDetails?.Quantity) {
          const quantity =
            requirementDetails.Quantity ||
            requirementDetails.RequestedQuantity ||
            0;
          filterTerms.QuantityGT = quantity - 50;
          filterTerms.QuantityLT = quantity + 50;
        }
        const destinationCountry = requirementDetails.DeliveryLocation
          ? requirementDetails.DeliveryLocation.split("~")[0] || ""
          : "";
        filterTerms.DestinationCountry = destinationCountry;

        console.log("Filter terms for offers:", filterTerms);

        // Handle price range filtering
        if (filters.searchPriceRange) {
          const [min, max] = filters.searchPriceRange.split("-");
          if (min === "Above") {
            filterTerms.PriceGT = max;
          } else {
            filterTerms.PriceGT = min;
            filterTerms.PriceLT = max;
          }
          delete filterTerms.searchPriceRange;
        }
        filterTerms.ExpiresGT = `'${dayjs().format("YYYY-MM-DD")}'`;
        let body = {
          target: "listGPOfferAdminDetails",
          filterTerms,
          userOrder: "CreatedAt DESC",
          rowStart: startRow,
          pageSize,
        };
        if (filterType === "Unmatched") {
          return;
        }
        if (filterType === "Matched") {
          body.filterTerms = {
            //OfferType: "Indicative",
            custom: `(GPOOfferStatus IS NULL OR GPOOfferStatus='' OR GPOOfferStatus='Indicative' OR GPOOfferStatus='Manual')`,
            ...filterTerms,
          };
        }
        if (filterType === "Request for Firm Offers") {
          filterTerms = {};
          body.target =
            userType === "Buyer"
              ? "listGPOfferByRequirement"
              : "listGPOfferByRequirementGP";
          body.groupby =
            userType === "Buyer" ? "BERequirementsID" : "GPRequirementsID";
          const statusCondition =
            userType === "Buyer"
              ? `BERequirementsID = '${requirementDetails.RequirementsID}' AND BECategoryType = 'rfq' AND BEOfferStatus = 'Firmoffer'`
              : `GPRequirementsID = '${requirementDetails.RequirementsID}' AND GPCategoryType = 'rfq' AND GPOfferStatus = 'Firmoffer'`;
          body.filterTerms = {
            custom: `${statusCondition} AND (ReferenceOffer IS NULL OR ReferenceOffer='')`,
            ...filterTerms,
          };
        }
        if (filterType === "Request for BAFO") {
          filterTerms = {};
          body.target =
            userType === "Buyer"
              ? "listGPOfferByRequirement"
              : "listGPOfferByRequirementGP";
          body.groupby =
            userType === "Buyer" ? "BERequirementsID" : "GPRequirementsID";
          const statusCondition =
            userType === "Buyer"
              ? `BERequirementsID = '${requirementDetails.RequirementsID}' AND BECategoryType = 'rfq' AND BEOfferStatus = 'BAFO'`
              : `GPRequirementsID = '${requirementDetails.RequirementsID}' AND GPCategoryType = 'rfq' AND GPOfferStatus = 'BAFO'`;
          body.filterTerms = {
            custom: `${statusCondition} AND (ReferenceOffer IS NULL OR ReferenceOffer='')`,
            ...filterTerms,
          };
        }
        if (filterType === "Request for Details") {
          filterTerms = {};
          body.target =
            userType === "Buyer"
              ? "listGPOfferByRequirement"
              : "listGPOfferByRequirementGP";
          body.groupby =
            userType === "Buyer" ? "BERequirementsID" : "GPRequirementsID";
          const statusCondition =
            userType === "Buyer"
              ? `BERequirementsID = '${requirementDetails.RequirementsID}' AND BECategoryType = 'rfq' AND BEOfferStatus = 'Query'`
              : `GPRequirementsID = '${requirementDetails.RequirementsID}' AND GPCategoryType = 'rfq' AND GPOfferStatus = 'Query'`;
          body.filterTerms = {
            custom: `${statusCondition} AND (ReferenceOffer IS NULL OR ReferenceOffer='')`,
            ...filterTerms,
          };
        }
        if (filterType === "Accepted Offers") {
          filterTerms = {};
          body.target =
            userType === "Buyer"
              ? "listGPOfferByRequirement"
              : "listGPOfferByRequirementGP";
          const statusCondition =
            userType === "Buyer"
              ? `BERequirementsID = '${requirementDetails.RequirementsID}' AND BECategoryType = 'rfq' AND BEOfferStatus = 'Accepted'`
              : `GPRequirementsID = '${requirementDetails.RequirementsID}' AND GPCategoryType = 'rfq' AND GPOfferStatus = 'Accepted'`;

          body.filterTerms = {
            custom: `${statusCondition} AND (ReferenceOffer IS NULL OR ReferenceOffer='')`,
            ...filterTerms,
          };
        }
        const response = await fetchData(body);

        console.log("Offers response:", response);

        if (response && response.items) {
          setOfferList(response.items);
          setTotalItems(response.total || 0);
          console.log("Found", response.items.length, "matching offers");
          // Reset selectedOffer when no offers available
          if (response.items.length === 0) {
            setSelectedOffer(null);
          }
        } else {
          console.log("No offers found in response");
          setOfferList([]);
          setTotalItems(0);
          setSelectedOffer(null);
        }
      } catch (error) {
        console.error("Error loading matching offers:", error);
        setOfferList([]);
        setTotalItems(0);
        setSelectedOffer(null);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to load matching offers. Please try again.",
          autoClose: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      pageSize,
      setLoading,
      productDetails,
      requirementDetails,
      filterType,
      userType,
    ]
  );

  // Table columns configuration
  const columns = [
    { id: "OfferNumber", numeric: false, label: "Offer No." },
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    {
      id: "Quantity",
      numeric: true,
      label: "Quantity (MT)",
      renderCell: (row) => formatPrice(row.Quantity),
    },
    {
      id: "FOBSalePrice",
      numeric: true,
      label: "FOB Sale Price (USD)",
      renderCell: (row) => row.FOBSalePrice,
    },
    { id: "Country", numeric: false, label: "Origin Country" },
    { id: "DestinationCountry", numeric: false, label: "Destination" },
    { id: "INCO", numeric: false, label: "INCO Terms" },
    // { id: "GPOOfferStatus", numeric: false, label: "Offer Status" },
    {
      id: "GPOOfferStatus",
      numeric: false,
      label: "Offer Type",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.GPOOfferStatus) {
          case "BAFO":
            bgColor = "#e0f2fe";
            textColor = "#0369a1";
            break;
          case "Firmoffer":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
            row.GPOOfferStatus = "Indicative"; // Set default status
            break;
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {row.GPOOfferStatus}
          </span>
        );
      },
    },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created Date",
      renderCell: (row) => formatDate(row.CreatedAt),
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires",
      renderCell: (row) => formatDate(row.Expires),
    },
  ];

  // Search filter fields
  const fields = [
    {
      name: "searchPriceRange",
      label: "Price Range (USD)",
      type: "dropdown",
      options: priceOptions,
      value: searchValues.searchPriceRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchPriceRange: value }));
        fetchMatchingOffers({ searchPriceRange: value }, 0);
      },
    },
  ];

  // Handle search
  const handleSearch = (searchValues) => {
    setRowStart(0);
    setCurrentPage(1);
    fetchMatchingOffers(searchValues, 0);
  };

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      const newRowStart = (page - 1) * pageSize;
      setRowStart(newRowStart);
      fetchMatchingOffers(searchValues, newRowStart);
    },
    [pageSize, searchValues, fetchMatchingOffers]
  );

  // Handle row click
  const handleRowClick = async (offer) => {
    setSelectedOffer(offer);
    console.log("selectedOffer@@@", selectedOffer);
    setTabValue(0); // Reset to first tab initially
    if (
      filterType === "Request for Firm Offers" ||
      filterType === "Request for BAFO"
    ) {
      await getReferenceOffers(offer);
    }
    if (filterType === "Accepted Offers") {
      const loadedReferenceOffers = await getReferenceOffers(offer);
      setTimeout(() => {
        // Determine which tab should be selected based on ReferenceOfferID
        const selectedTabIndex = determineSelectedTab(
          offer,
          loadedReferenceOffers
        );
        setTabValue(selectedTabIndex);
      }, 300);
    }
  };

  // Handle PDF download for accepted offers
  const handleDownloadOffer = useCallback(
    async (referenceOffer) => {
      if (!selectedOffer) {
        console.error("No offer selected for PDF generation");
        return;
      }

      setLoading(true);

      try {
        console.log("Generating PDF for offer:", selectedOffer);

        // Prepare offer data for PDF generation
        const offerData = {
          ...selectedOffer,
          // Add any additional data needed for the PDF
          BuyerName: "Admin User", // You might want to get actual admin info
          BuyerAddress: "Admin Address",
          BuyerContact: "Admin Contact",
          // Add reference offer data if available
          ...(referenceOffer && { ReferenceOffer: referenceOffer }),
        };

        const body = {
          target: "generateOfferPDF",
          offerData: offerData,
        };

        console.log("Calling PDF generation API with:", body);

        const result = await postQuery(body, "backendops");

        if (result && result.success) {
          console.log("PDF generated successfully");

          // Convert base64 to blob and download
          const base64Data = result.pdf;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          // Create download link
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download =
            result.filename ||
            `offer-agreement-${
              selectedOffer.OfferNumber ||
              selectedOffer.RequirementNumber ||
              "document"
            }.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          setModal({
            show: true,
            type: "success",
            title: "Success",
            message: "PDF downloaded successfully!",
            autoClose: false,
          });
        } else {
          throw new Error(result?.error || "PDF generation failed");
        }
      } catch (error) {
        console.error("Error generating PDF:", error);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to generate PDF. Please try again.",
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [selectedOffer, setLoading]
  );

  const handleAgreementPopup = useCallback(
    async (referenceOffer) => {
      const offerToUse = referenceOffer || selectedOffer;

      if (!offerToUse) {
        console.error("No offer selected for agreement generation");
        return;
      }

      setLoading(true);

      try {
        // Fetch user data using the specified payload
        const body = {
          target: "getUserInfoByEmail",
          EmailID: referenceOffer.OfferFor || selectedOffer.BuyerEmailID,
        };

        console.log("Fetching user info for agreement:", body);
        const userDataResponse = await fetchData(body);

        console.log("User data response:", userDataResponse);
        console.log("offerToUse:", offerToUse);

        // Extract user information from the response
        const userData = userDataResponse?.data || {};

        // Extract and prepare offer data for the agreement
        const agreementData = extractOfferDataForAgreement({
          ...offerToUse,
          // Add buyer information from fetched user data (for admin, might be different)
          buyerName:
            userData.FullName ||
            `${userData.FirstName || ""} ${userData.LastName || ""}`.trim() ||
            "Admin User",
          buyerAddress:
            userData.Address1 || userData.CompanyAddress1 || "Admin Address",
          buyerContact:
            `${userData.Phone1}, ${userData.EmailID}` ||
            userData.ContactNumber ||
            "Admin Contact",
        });

        console.log("Agreement data prepared:", agreementData);

        // Open the agreement popup
        openAgreementPopup(agreementData);
      } catch (error) {
        console.error("Error opening agreement popup:", error);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to open agreement. Please try again.",
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [selectedOffer, setLoading]
  );

  // Button component for download actions
  const DownloadButton = ({ label, onClick }) => {
    return (
      <Button
        variant="contained"
        onClick={onClick}
        style={{
          backgroundColor: "#de6b14",
          color: "#fff",
          borderRadius: "6px",
          padding: "8px 16px",
          textTransform: "none",
          fontWeight: "500",
          whiteSpace: "nowrap",
          width: "140px",
        }}
      >
        {label}
      </Button>
    );
  };

  // Handler for creating offer from reference offer
  const handleCreateOfferFromReference = useCallback(
    (referenceOffer) => {
      console.log("Creating offer from reference offer:", referenceOffer);
      setPreviousTabValue(tabValue); // Store current tab to return to later
      setSelectedReferenceOffer(referenceOffer);
      setShowCreateOfferForm(true);
    },
    [tabValue]
  );

  // Effects
  useEffect(() => {
    console.log(
      "Initializing product details for requirement:",
      requirementDetails
    );
    getRequirementProductInfo();
  }, [getRequirementProductInfo, requirementDetails, allProductMaster]);

  useEffect(() => {
    console.log(
      "Triggering fetchMatchingOffers with productDetails:",
      productDetails
    );
    if (productDetails && productDetails.ProductName) {
      fetchMatchingOffers(searchValues, rowStart);
    }
  }, [productDetails, searchValues, fetchMatchingOffers, rowStart]);

  return (
    <div className="matching-offers-container" style={{ marginTop: "20px" }}>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      {filterType === "Unmatched" ? (
        // Show Create Offer Form for Unmatched requirements
        <div className="card">
          <CreateOfferForm
            requirementDetails={requirementDetails}
            filterType={filterType}
            onDeselectRequirement={onDeselectRequirement}
            onClose={() => {}}
            onSuccess={() => {
              // Refresh parent requirements table when filterType is unmatched
              if (filterType === "Unmatched" && onRequirementsRefresh) {
                onRequirementsRefresh();
              }
            }}
          />
        </div>
      ) : filterType === "Request for Details" ? (
        // Show Matching Offers List for Request for Details (same as matched offers)
        <>
          <div className="search-filter4">
            <div className="filter4">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
          </div>

          <DataTable
            columns={columns}
            data={offerList}
            total={totalItems}
            title="Matching Offers"
            rowsPerPage={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onRowClick={handleRowClick}
            selectedRows={
              selectedOffer ? [selectedOffer.GPOfferID || selectedOffer.id] : []
            }
          />

          {selectedOffer && offerList.length > 0 && (
            <div className="offer-detail1" style={{ marginTop: "20px" }}>
              <div className="header-text-re">
                <div className="head-text">Offer Details</div>
                {selectedOffer ? (
                  selectedOffer.IsShortlistRequirement === 1 ? (
                    <div
                      className="shortlist-text"
                      style={{
                        background: "#27ae60",
                        color: "#fff",
                        borderRadius: "6px",
                        padding: "4px 14px",
                        display: "inline-block",
                      }}
                    >
                      Shortlisted
                    </div>
                  ) : (
                    <div className="button-group">
                      {/* Buttons removed as requested */}
                    </div>
                  )
                ) : (
                  <div></div>
                )}
              </div>

              <OfferDetails
                offer={selectedOffer}
                btnShortlist={false}
                btnSendQuery={false}
                btnRequestQuote={false}
                btnRequestSpecifications={false}
                btnRequestBestPrice={false}
                btnNegotiate={false}
                btnRequestShippingDetails={false}
                btnLogisticsSupport={false}
                btnRaiseQuery={false}
              />
            </div>
          )}
        </>
      ) : filterType === "Request for Firm Offers" ||
        filterType === "Request for BAFO" ||
        filterType === "Accepted Offers" ? (
        // Show Matching Offers List for Request for Firm Offers (same as matched offers)
        showCreateOfferForm ? (
          <div className="create-offer-form-container">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h6>
                Create Offer Based on Offer #
                {selectedReferenceOffer
                  ? selectedReferenceOffer.OfferNumber
                  : selectedOffer?.OfferNumber}
              </h6>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowCreateOfferForm(false);
                  setSelectedReferenceOffer(null);
                }}
                // style={{ color: "#de6b14", borderColor: "#de6b14" }}
                className="submit-btn-more"
              >
                Back to Offers
              </Button>
            </div>
            <CreateOfferForm
              requirementDetails={requirementDetails}
              offerDetails={selectedReferenceOffer || selectedOffer}
              filterType={filterType}
              onDeselectRequirement={onDeselectRequirement}
              onClose={() => {
                setShowCreateOfferForm(false);
                setSelectedReferenceOffer(null);
              }}
              onSuccess={() => {
                setShowCreateOfferForm(false);
                setSelectedReferenceOffer(null);
                setTabValue(previousTabValue); // Return to the previous tab
                // Refresh offers list
                fetchMatchingOffers(searchValues, rowStart);
                // Reload reference offers by calling handleRowClick
                if (selectedOffer) {
                  handleRowClick(selectedOffer);
                }
                // Refresh parent requirements table when filterType is unmatched
                if (filterType === "Unmatched" && onRequirementsRefresh) {
                  onRequirementsRefresh();
                }
              }}
            />
          </div>
        ) : (
          <>
            <div className="search-filter4">
              <div className="filter4">
                <SearchFilter fields={fields} onSearch={handleSearch} />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={offerList}
              total={totalItems}
              title="Matching Offers"
              rowsPerPage={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onRowClick={handleRowClick}
              selectedRows={
                selectedOffer
                  ? [selectedOffer.GPOfferID || selectedOffer.id]
                  : []
              }
            />

            {selectedOffer && offerList.length > 0 && (
              <div className="offer-detail1" style={{ marginTop: "20px" }}>
                {/* Tab Navigation */}
                {referenceOffers.length > 0 ? (
                  <>
                    <Box sx={{ borderBottom: 1, borderColor: "#de6b14" }}>
                      <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="Offer info tabs"
                        sx={{
                          "& .MuiTab-root": {
                            color: "#000",
                            backgroundColor: "transparent",
                            "&.Mui-selected": {
                              color: "#fff",
                              backgroundColor: "#de6b14",
                              borderBottom: "2px solid #d4d4d4",
                            },
                          },
                          "& .MuiTabs-indicator": {
                            backgroundColor: "#de6b14",
                          },
                        }}
                      >
                        <Tab
                          label={
                            <div style={{ textAlign: "center" }}>
                              <div>
                                {selectedOffer.OfferNumber}{" "}
                                {selectedOffer.CreatedAt
                                  ? `(${formatDate(selectedOffer.CreatedAt)})`
                                  : ""}
                              </div>

                              <div style={{ fontSize: "8px", opacity: 0.7 }}>
                                Initial Offer
                              </div>
                            </div>
                          }
                          id="tab-0"
                          aria-controls="tabpanel-0"
                        />
                        {referenceOffers.map((offer, index) => (
                          <Tab
                            key={index}
                            label={
                              <div style={{ textAlign: "center" }}>
                                <div>
                                  {referenceOffers[index].OfferNumber}{" "}
                                  {referenceOffers[index].CreatedAt
                                    ? `(${formatDate(
                                        referenceOffers[index].CreatedAt
                                      )})`
                                    : ""}
                                </div>
                                {selectedOffer.ReferenceOfferID ===
                                referenceOffers[index].GPOfferID ? (
                                  <div
                                    style={{ fontSize: "8px", opacity: 0.7 }}
                                  >
                                    Offer V{index + 2} - Accepted
                                  </div>
                                ) : referenceOffers[index]
                                    .BuyerExtendedStatus === "Firmoffer" ||
                                  referenceOffers[index].GPExtendedStatus ===
                                    "Firmoffer" ? (
                                  <div
                                    style={{ fontSize: "8px", opacity: 0.7 }}
                                  >
                                    Offer V{index + 2} - Firm Raised
                                  </div>
                                ) : referenceOffers[index]
                                    .BuyerExtendedStatus === "BAFO" ||
                                  referenceOffers[index].GPExtendedStatus ===
                                    "BAFO" ? (
                                  <div
                                    style={{ fontSize: "8px", opacity: 0.7 }}
                                  >
                                    Offer V{index + 2} - BAFO Raised
                                  </div>
                                ) : (
                                  <div
                                    style={{ fontSize: "8px", opacity: 0.7 }}
                                  >
                                    Offer V{index + 2}
                                  </div>
                                )}
                              </div>
                            }
                            id={`tab-${index + 1}`}
                            aria-controls={`tabpanel-${index + 1}`}
                          />
                        ))}
                      </Tabs>
                    </Box>

                    {/* Tab Content */}
                    <TabPanel value={tabValue} index={0}>
                      <div className="offer-detail1">
                        <div className="header-text-re">
                          <div className="head-text">Offer Details</div>
                          <div className="button-group">
                            {/* Show download button only if this is the target tab */}
                            {filterType === "Accepted Offers" &&
                              shouldShowDownloadButton(
                                0,
                                selectedOffer,
                                referenceOffers
                              ) && (
                                <>
                                  {/* <DownloadButton
                                  label="Download Offer"
                                  onClick={() => handleDownloadOffer()}
                                /> */}
                                  <DownloadButton
                                    label="View Agreement"
                                    onClick={() => handleAgreementPopup()}
                                  />
                                </>
                              )}
                          </div>
                        </div>
                        <OfferDetails
                          offer={selectedOffer}
                          btnShortlist={false}
                          btnSendQuery={false}
                          btnRequestQuote={false}
                          btnRequestSpecifications={false}
                          btnRequestBestPrice={false}
                          btnNegotiate={false}
                          btnRequestShippingDetails={false}
                          btnLogisticsSupport={false}
                          btnRaiseQuery={false}
                        />
                      </div>
                    </TabPanel>

                    {referenceOffers.map((referenceOffer, index) => (
                      <TabPanel key={index} value={tabValue} index={index + 1}>
                        <div className="offer-detail1">
                          <div className="header-text-re">
                            <div className="head-text">
                              Reference Offer Details {index + 2}
                            </div>
                            <div className="button-group">
                              {/* Show download button only if this is the target tab */}
                              {filterType === "Accepted Offers" &&
                                shouldShowDownloadButton(
                                  index + 1,
                                  selectedOffer,
                                  referenceOffers
                                ) && (
                                  <>
                                    {/* <DownloadButton
                                    label="Download Offer"
                                    onClick={() =>
                                      handleDownloadOffer(referenceOffer)
                                    }
                                  /> */}
                                    <DownloadButton
                                      label="View Agreement"
                                      onClick={() =>
                                        handleAgreementPopup(referenceOffer)
                                      }
                                    />
                                  </>
                                )}

                              {/* Show create offer button for last reference offer with IsFirmReferenced */}
                              {shouldShowCreateOfferButton(
                                index + 1,
                                referenceOffers
                              ) && (
                                <Button
                                  variant="contained"
                                  onClick={() =>
                                    handleCreateOfferFromReference(
                                      referenceOffer
                                    )
                                  }
                                  style={{
                                    backgroundColor: "#de6b14",
                                    color: "#fff",
                                    borderRadius: "6px",
                                    padding: "8px 16px",
                                    textTransform: "none",
                                    fontWeight: "500",
                                    whiteSpace: "nowrap",
                                    marginLeft: "10px",
                                  }}
                                >
                                  Create Offer
                                </Button>
                              )}
                            </div>
                          </div>
                          <OfferDetails
                            offer={referenceOffer}
                            btnShortlist={false}
                            btnSendQuery={false}
                            btnRequestQuote={false}
                            btnRequestSpecifications={false}
                            btnRequestBestPrice={false}
                            btnNegotiate={false}
                            btnRequestShippingDetails={false}
                            btnLogisticsSupport={false}
                            btnRaiseQuery={false}
                          />
                        </div>
                      </TabPanel>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="header-text-re">
                      <div className="head-text">Offer Details</div>
                      <div className="button-group">
                        <Button
                          variant="contained"
                          onClick={() => {
                            setPreviousTabValue(tabValue); // Store current tab to return to later
                            setShowCreateOfferForm(true);
                          }}
                          style={{
                            backgroundColor: "#de6b14",
                            color: "#fff",
                            borderRadius: "6px",
                            padding: "8px 16px",
                            textTransform: "none",
                            fontWeight: "500",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Create Offer
                        </Button>
                      </div>
                    </div>
                    <OfferDetails
                      offer={selectedOffer}
                      btnShortlist={false}
                      btnSendQuery={false}
                      btnRequestQuote={false}
                      btnRequestSpecifications={false}
                      btnRequestBestPrice={false}
                      btnNegotiate={false}
                      btnRequestShippingDetails={false}
                      btnLogisticsSupport={false}
                      btnRaiseQuery={false}
                    />
                  </>
                )}
              </div>
            )}
          </>
        )
      ) : filterType === "Request for Firm Offers" ||
        filterType === "Request for BAFO" ? (
        // Show Matching Offers List for Request for BAFO (same as matched offers)
        showCreateOfferForm ? (
          <div className="create-offer-form-container">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h6>
                Create Offer Based on Offer #
                {selectedReferenceOffer
                  ? selectedReferenceOffer.OfferNumber
                  : selectedOffer?.OfferNumber}
              </h6>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowCreateOfferForm(false);
                  setSelectedReferenceOffer(null);
                }}
                style={{ color: "#de6b14", borderColor: "#de6b14" }}
              >
                Back to Offers
              </Button>
            </div>
            <CreateOfferForm
              requirementDetails={requirementDetails}
              offerDetails={selectedReferenceOffer || selectedOffer}
              filterType={filterType}
              onDeselectRequirement={onDeselectRequirement}
              onClose={() => {
                setShowCreateOfferForm(false);
                setSelectedReferenceOffer(null);
              }}
              onSuccess={() => {
                setShowCreateOfferForm(false);
                setSelectedReferenceOffer(null);
                setTabValue(previousTabValue); // Return to the previous tab
                // Refresh offers list
                fetchMatchingOffers(searchValues, rowStart);
                // Reload reference offers by calling handleRowClick
                if (selectedOffer) {
                  handleRowClick(selectedOffer);
                }
                // Refresh parent requirements table when filterType is unmatched
                if (filterType === "Unmatched" && onRequirementsRefresh) {
                  onRequirementsRefresh();
                }
              }}
            />
          </div>
        ) : (
          <>
            <div className="search-filter4">
              <div className="filter4">
                <SearchFilter fields={fields} onSearch={handleSearch} />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={offerList}
              total={totalItems}
              title="Matching Offers"
              rowsPerPage={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onRowClick={handleRowClick}
              selectedRows={
                selectedOffer
                  ? [selectedOffer.GPOfferID || selectedOffer.id]
                  : []
              }
            />

            {selectedOffer && offerList.length > 0 && (
              <div className="offer-detail1" style={{ marginTop: "20px" }}>
                <div className="header-text-re">
                  <div className="head-text">Offer Details</div>
                  <div className="button-group">
                    <Button
                      variant="contained"
                      onClick={() => {
                        setPreviousTabValue(tabValue); // Store current tab to return to later
                        setShowCreateOfferForm(true);
                      }}
                      style={{
                        backgroundColor: "#de6b14",
                        color: "#fff",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        textTransform: "none",
                        fontWeight: "500",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Create Offer
                    </Button>
                  </div>
                </div>

                <OfferDetails
                  offer={selectedOffer}
                  btnShortlist={false}
                  btnSendQuery={false}
                  btnRequestQuote={false}
                  btnRequestSpecifications={false}
                  btnRequestBestPrice={false}
                  btnNegotiate={false}
                  btnRequestShippingDetails={false}
                  btnLogisticsSupport={false}
                  btnRaiseQuery={false}
                />
              </div>
            )}
          </>
        )
      ) : (
        // Show Matching Offers List for Matched requirements (default)
        <>
          <div className="search-filter4">
            <div className="filter4">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
          </div>

          <DataTable
            columns={columns}
            data={offerList}
            total={totalItems}
            title="Matching Offers"
            rowsPerPage={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onRowClick={handleRowClick}
            selectedRows={
              selectedOffer ? [selectedOffer.GPOfferID || selectedOffer.id] : []
            }
          />

          {selectedOffer && offerList.length > 0 && (
            <div className="offer-detail1" style={{ marginTop: "20px" }}>
              <div className="header-text-re">
                <div className="head-text">Offer Details</div>
                {selectedOffer ? (
                  selectedOffer.IsShortlistRequirement === 1 ? (
                    <div
                      className="shortlist-text"
                      style={{
                        background: "#27ae60",
                        color: "#fff",
                        borderRadius: "6px",
                        padding: "4px 14px",
                        display: "inline-block",
                      }}
                    >
                      Shortlisted
                    </div>
                  ) : (
                    <div className="button-group">
                      {/* Buttons removed as requested */}
                    </div>
                  )
                ) : (
                  <div></div>
                )}
              </div>

              <OfferDetails
                offer={selectedOffer}
                btnShortlist={false}
                btnSendQuery={false}
                btnRequestQuote={false}
                btnRequestSpecifications={false}
                btnRequestBestPrice={false}
                btnNegotiate={false}
                btnRequestShippingDetails={false}
                btnLogisticsSupport={false}
                btnRaiseQuery={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MatchingOffers;
